import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  KnowledgeBaseState,
  KnowledgeBaseLinkListParams,
  KnowledgeBaseLinkListResponse,
  KnowledgeBaseLinkActionResponse,
  CreateKnowledgeBaseLinkRequest,
  UpdateKnowledgeBaseLinkRequest,
  KnowledgeBaseLink,
} from '@/constants/types/knowledgeBase.types';
import { knowledgeBaseService } from '@/services/admin/knowledgeBaseService';

// Initial State
const initialState: KnowledgeBaseState = {
  links: [],
  pagination: null,
  isLoading: false,
  isActionLoading: false,
  isUploading: false,
  error: null,
  currentLink: null,
};

// Async Thunks
export const fetchKnowledgeBaseLinks = createAsyncThunk(
  'knowledgeBase/fetchLinks',
  async (params: KnowledgeBaseLinkListParams, { rejectWithValue }) => {
    try {
      const response = await knowledgeBaseService.getLinks(params);
      return response;
    } catch (error: any) {
      console.error('Error fetching knowledge base links:', error);
      const errorMessage = error?.message || 'Failed to fetch knowledge base links';
      return rejectWithValue(errorMessage);
    }
  }
);

export const createKnowledgeBaseLink = createAsyncThunk(
  'knowledgeBase/createLink',
  async (data: CreateKnowledgeBaseLinkRequest, { rejectWithValue }) => {
    try {
      const response = await knowledgeBaseService.createLink(data);
      return response;
    } catch (error: any) {
      console.error('Error creating knowledge base link:', error);
      const errorMessage = error?.message || 'Failed to create knowledge base link';
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateKnowledgeBaseLink = createAsyncThunk(
  'knowledgeBase/updateLink',
  async (
    { id, data }: { id: string; data: UpdateKnowledgeBaseLinkRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await knowledgeBaseService.updateLink(id, data);
      return { id, ...response };
    } catch (error: any) {
      console.error('Error updating knowledge base link:', error);
      const errorMessage = error?.message || 'Failed to update knowledge base link';
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteKnowledgeBaseLink = createAsyncThunk(
  'knowledgeBase/deleteLink',
  async (id: string, { rejectWithValue }) => {
    try {
      await knowledgeBaseService.deleteLink(id);
      return id;
    } catch (error: any) {
      console.error('Error deleting knowledge base link:', error);
      const errorMessage = error?.message || 'Failed to delete knowledge base link';
      return rejectWithValue(errorMessage);
    }
  }
);

// Slice
const knowledgeBaseSlice = createSlice({
  name: 'knowledgeBase',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetKnowledgeBaseState: () => initialState,
    setCurrentLink: (state, action: PayloadAction<KnowledgeBaseLink | null>) => {
      state.currentLink = action.payload;
    },
    setUploading: (state, action: PayloadAction<boolean>) => {
      state.isUploading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Links
      .addCase(fetchKnowledgeBaseLinks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchKnowledgeBaseLinks.fulfilled,
        (state, action: PayloadAction<KnowledgeBaseLinkListResponse>) => {
          state.isLoading = false;
          state.links = action.payload.data.links;
          state.pagination = action.payload.data.pagination;
          state.error = null;
        }
      )
      .addCase(fetchKnowledgeBaseLinks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Create Link
      .addCase(createKnowledgeBaseLink.pending, (state) => {
        state.isActionLoading = true;
        state.error = null;
      })
      .addCase(
        createKnowledgeBaseLink.fulfilled,
        (state, action: PayloadAction<KnowledgeBaseLinkActionResponse>) => {
          state.isActionLoading = false;
          if (action.payload.data?.link) {
            state.links.unshift(action.payload.data.link);
          }
          state.error = null;
        }
      )
      .addCase(createKnowledgeBaseLink.rejected, (state, action) => {
        state.isActionLoading = false;
        state.error = action.payload as string;
      })

      // Update Link
      .addCase(updateKnowledgeBaseLink.pending, (state) => {
        state.isActionLoading = true;
        state.error = null;
      })
      .addCase(updateKnowledgeBaseLink.fulfilled, (state, action: PayloadAction<any>) => {
        state.isActionLoading = false;
        const { id, data } = action.payload;
        const linkIndex = state.links.findIndex((l) => l._id === id);
        if (linkIndex !== -1 && data?.link) {
          state.links[linkIndex] = data.link;
        }
        state.currentLink = null;
        state.error = null;
      })
      .addCase(updateKnowledgeBaseLink.rejected, (state, action) => {
        state.isActionLoading = false;
        state.error = action.payload as string;
      })

      // Delete Link
      .addCase(deleteKnowledgeBaseLink.pending, (state) => {
        state.isActionLoading = true;
        state.error = null;
      })
      .addCase(
        deleteKnowledgeBaseLink.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.isActionLoading = false;
          state.links = state.links.filter((l) => l._id !== action.payload);
          if (state.pagination) {
            state.pagination.total -= 1;
          }
          state.error = null;
        }
      )
      .addCase(deleteKnowledgeBaseLink.rejected, (state, action) => {
        state.isActionLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetKnowledgeBaseState, setCurrentLink, setUploading } =
  knowledgeBaseSlice.actions;
export default knowledgeBaseSlice.reducer;
